import {
    Input,
    Component,
    ContentChild,
    TemplateRef,
    ElementRef,
    Output,
    EventEmitter,
    ContentChildren,
    QueryList,
    AfterContentInit,
    OnDestroy
} from '@angular/core';
import { Subscription } from 'rxjs';
import { take } from 'rxjs/operators';
import { SortType } from '../../constants-types/Enums';
import { SearchResult } from '../../core-data-model/SearchResult.model';
import { SearchResultSet } from '../../core-data-model/SearchResultSet';
import { Table } from '../../core-services/Tables/Table';
import {
    RegisterColumnDirective,
    ShowColumnDirective
} from './ColumnDefinition.component';
import { SortableHeaderComponent } from './SortableHeader.component';

@Component({
    selector: 'pi-table',
    template: `
        @if (selection) {
            <pi-table-global-select
                [table]="table"
                [selection]="selection"
                (selectionChanged)="selectionChange($event)"
            >
            </pi-table-global-select>
        }
        <table>
            <thead>
                <tr
                    class="pi-table_head"
                    [ngStyle]="{ cursor: selection ? 'pointer' : 'auto' }"
                >
                    @if (selection) {
                        <th
                            class="pi-table_cell--tinySize pi-table_cell--centered"
                        >
                            <ok-checkbox
                                [ngModel]="allResultsSelected"
                                (ngModelChange)="headerCheckClicked($event)"
                            >
                            </ok-checkbox>
                        </th>
                    }
                    <ng-template
                        [ngTemplateOutlet]="headerTemplate"
                        [ngTemplateOutletContext]="{}"
                    >
                    </ng-template>
                </tr>
            </thead>
            <tbody class="pi-table_body">
                @for (item of table.contents; track item) {
                    <tr
                        class="pi-table_row"
                        (mouseenter)="rowHover.emit(item)"
                        (mouseleave)="rowHover.emit(null)"
                        (click)="rowClick(item)"
                    >
                        @if (selection) {
                            <td
                                class="pi-table_cell--tinySize pi-table_cell--centered"
                            >
                                <ok-checkbox
                                    [ngModel]="isItemSelected(item)"
                                    (ngModelChange)="toggleSelection(item)"
                                >
                                </ok-checkbox>
                            </td>
                        }
                        <ng-template
                            [ngTemplateOutlet]="dataRowTemplate"
                            [ngTemplateOutletContext]="{
                                item: item
                            }"
                        >
                        </ng-template>
                    </tr>
                }
            </tbody>
        </table>
        @if (table.loading) {
            <loading-dots> </loading-dots>
        }
    `,
    standalone: false
})
export class TableComponent implements AfterContentInit, OnDestroy {
    @ContentChild('dataRow', { static: false })
    dataRowTemplate: TemplateRef<ElementRef>;
    @ContentChild('header', { static: false })
    headerTemplate: TemplateRef<ElementRef>;
    @ContentChildren(RegisterColumnDirective)
    registeredColumn: QueryList<RegisterColumnDirective>;
    @ContentChildren(ShowColumnDirective)
    showColumn: QueryList<ShowColumnDirective>;
    @ContentChildren(SortableHeaderComponent)
    sortableColumns: QueryList<SortableHeaderComponent>;

    @Input()
    table: Table<SearchResult>;
    @Input()
    selection: SearchResultSet;
    @Input()
    identityForSettings: string;
    @Output()
    readonly rowHover = new EventEmitter<any>();
    @Output()
    readonly onRowClick = new EventEmitter<any>();
    @Output()
    readonly selectionChanged = new EventEmitter<SearchResultSet>();

    individualColumnSubs: Subscription[] = [];

    get allResultsSelected(): boolean {
        return (
            this.selection &&
            this.selection.allResultsInContext &&
            this.selection.allResultsInContext(this.table.contents)
        );
    }

    get isItemSelected() {
        return (item: SearchResult): boolean =>
            this.selection &&
            this.selection.isInResultSet &&
            this.selection.isInResultSet(item);
    }

    selectionChange(newSelection: SearchResultSet) {
        this.selectionChanged.emit(newSelection);
    }

    rowClick(item: any): void {
        this.onRowClick.emit(item);
        if (this.selection && this.selection.toggleFromResultSet) {
            this.selectionChanged.emit(
                this.selection.toggleFromResultSet(item)
            );
        }
    }

    headerCheckClicked(checked: boolean) {
        if (checked && this.selection && this.selection.addRowSet) {
            this.selectionChanged.emit(
                this.selection.addRowSet(this.table.contents)
            );
        } else if (!checked && this.selection && this.selection.removeRowSet) {
            this.selectionChanged.emit(
                this.selection.removeRowSet(this.table.contents)
            );
        }
    }

    toggleSelection(item: SearchResult) {
        if (this.selection && this.selection.toggleFromResultSet) {
            this.selectionChanged.emit(
                this.selection.toggleFromResultSet(item)
            );
        }
    }

    ngAfterContentInit() {
        this.processSortableColumns();
        this.processColumnRegistration();
    }

    processColumnRegistration() {
        // Had to do this via subscription
        this.registeredColumn.changes.subscribe((listToRegister) => {
            listToRegister.forEach((column: RegisterColumnDirective) => {
                this.table.columnInfo.registerColumn(column.tableInfo);
                column.table = this.table;
            });
        });
        this.showColumn.changes.subscribe((listToRegister) => {
            listToRegister.forEach((column: ShowColumnDirective) => {
                column.table = this.table;
            });
        });
    }

    processSortableColumns() {
        // Note: The take(1) pipe was added in order to fix https://predictiveindex.atlassian.net/browse/PI2-2972
        // this.table.addSort(sortType) triggers search which updates the DOM and hence the contentchildren [sortableColumns]
        // This results in new duplicate subscription to sortablecolumns and from here both sortableColumns and sortColumnsubscriptions grow exponentilly
        // leading to network bottleneck  as each addSort triggers search calling API and fail sorting
        this.sortableColumns.changes.pipe(take(1)).subscribe((listToSort) => {
            listToSort.forEach((sortColumn: SortableHeaderComponent) => {
                sortColumn.sortState = this.table.searchState.sortingState;
                this.individualColumnSubs.push(
                    sortColumn.sort.subscribe((sortType: SortType) => {
                        this.table.addSort(sortType);
                    })
                );
            });
        });
    }

    ngOnDestroy() {
        if (this.individualColumnSubs) {
            this.individualColumnSubs.forEach((x) => {
                x.unsubscribe();
            });
        }
    }
}
